import api from "./axios";
import type {
  KerchiefReportRow,
  CamperKerchiefData,
  IndividualSummaryRow,
  CabinSummaryRow,
  ActivitySummaryRow,
  CeremonyReportResponse,
  AcBookActivity,
  TeamReportData,
  TrendData,
  StampingReportResponse,
  AcDifficultyRankings,
} from "../types";

/**
 * Builds the query params for term-filtered report endpoints.
 *
 * - termId non-null → filter by that specific term
 * - termId null     → "All Campers", fall back to year-only filter
 */
function termParam(termId: number | null, year: number): Record<string, unknown> {
  return termId !== null ? { termId } : { year };
}

export const reportsApi = {
  kerchief: async (params: {
    termId: number | null;
    year: number;
    divisionId?: number;
    cabinGroupId?: number;
  }): Promise<KerchiefReportRow[]> => {
    const { termId, year, ...rest } = params;
    const res = await api.get<KerchiefReportRow[]>("/reports/kerchief", {
      params: { ...termParam(termId, year), ...rest },
    });
    return res.data;
  },

  camperKerchief: async (camperId: number): Promise<CamperKerchiefData> => {
    const res = await api.get<CamperKerchiefData>(`/reports/kerchief/${camperId}`);
    return res.data;
  },

  individualSummary: async (termId: number | null, year: number): Promise<IndividualSummaryRow[]> => {
    const res = await api.get<IndividualSummaryRow[]>("/reports/individual", {
      params: termParam(termId, year),
    });
    return res.data;
  },

  cabinSummary: async (termId: number | null, year: number, cabinGroupId?: number): Promise<CabinSummaryRow[]> => {
    const res = await api.get<CabinSummaryRow[]>("/reports/cabin", {
      params: { ...termParam(termId, year), ...(cabinGroupId ? { cabinGroupId } : {}) },
    });
    return res.data;
  },

  activitySummary: async (termId: number | null, year: number, lifetime: boolean = false): Promise<ActivitySummaryRow[]> => {
    const res = await api.get<ActivitySummaryRow[]>("/reports/activity-summary", {
      params: { ...termParam(termId, year), lifetime },
    });
    return res.data;
  },

  ceremony: async (termId: number | null, year: number): Promise<CeremonyReportResponse | null> => {
    if (termId === null) return null;  // No ceremony report for "All Campers"
    const res = await api.get<CeremonyReportResponse>("/reports/ceremony", {
      params: { termId },
    });
    return res.data;
  },

  acBook: async (termId: number | null, activityId?: number): Promise<AcBookActivity[]> => {
    if (termId === null) return [];  // AC Book needs a specific term for cabin plan
    const res = await api.get<AcBookActivity[]>("/reports/ac-book", {
      params: { termId, ...(activityId ? { activityId } : {}) },
    });
    return res.data;
  },

  teamSummary: async (termId: number | null, year: number): Promise<TeamReportData> => {
    if (termId === null) {
      // Return empty structure for "All Campers" (no cabin plan to group by)
      return { overall: { army: 0, navy: 0, unassigned: 0, total: 0 }, byDivision: [], byCabin: [] };
    }
    const res = await api.get<TeamReportData>("/reports/team-summary", {
      params: { termId },
    });
    return res.data;
  },

  trends: async (): Promise<TrendData> => {
    const res = await api.get<TrendData>("/reports/trends");
    return res.data;
  },

  acDifficultyRankings: async (params: {
    scope: "lifetime" | "term";
    termId?: number | null;
    year?: number;
  }): Promise<AcDifficultyRankings> => {
    const { scope, termId, year } = params;
    const query: Record<string, unknown> = { scope };
    if (scope === "term") {
      if (termId != null) query.termId = termId;
      else if (year != null) query.year = year;
    }
    const res = await api.get<AcDifficultyRankings>("/reports/ac-difficulty-rankings", { params: query });
    return res.data;
  },

  kerchiefStamping: async (termId: number): Promise<StampingReportResponse> => {
    const res = await api.get<StampingReportResponse>("/reports/kerchief-stamping", {
      params: { termId },
    });
    return res.data;
  },
};
